"use client";

import Translations from "@translate/components/Chat/BusinessComponents/AssetHistoricalEarningsBusinessChart";

import { useEarningsData } from "@hooks/useEarningsData";
import { useHistoricalEarningsData } from "@hooks/useHistoricalEarningsData";
import { useStockDetailsMarket } from "@hooks/useStockDetailsMarket";
import { EARNINGS_QUERY_PARAMS } from "@config/stock";
import { I18nUtil } from "@utils/i18n";
import React from "react";
import { StockDetailTable } from "@components/StockDetails/StockDetailTable";
import { ViewDetailsLink, buildStockDetailModuleHref } from "./ViewDetailsLink";

const I18n = I18nUtil.createScoped(Translations);

interface AssetHistoricalEarningsBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetHistoricalEarningsBusinessChart: React.FC<
  AssetHistoricalEarningsBusinessChartProps
> = ({ assetRouterId }) => {
  const { data: stockData, isLoading } = useStockDetailsMarket({
    queryParams: { ...EARNINGS_QUERY_PARAMS, ids: [assetRouterId] },
    queryKey: ["chat-earnings", assetRouterId],
  });

  const stockSymbol = stockData?.symbol || "";
  const { validQuarters } = useEarningsData(stockData?.earnings?.items);

  const {
    displayData,
    columns,
    selectedPeriod,
    selectedMetric,
    setSelectedPeriod,
    setSelectedMetric,
  } = useHistoricalEarningsData(validQuarters, true);

  if (isLoading) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!validQuarters?.length) {
    return null;
  }

  return (
    <>
      <div className="flex w-full flex-col overflow-hidden">
        <div className="flex flex-row items-center gap-3">
          <tab-indicator
            className="md_bill_switch bg-background_white_overlay10 md:[--capsule-bg-color:white] [--capsule-bg-color:var(--edgen-background\_brand\_overlay100)] [--capsule-border-radius:200px] [--animation-duration:0.2s]"
            effect="capsule"
          >
            <div
              role="tab-item"
              className="switch_group_atoms md_switch_group_atoms"
              data-actived={selectedPeriod === "quarterly"}
              onClick={() => setSelectedPeriod("quarterly")}
            >
              <div className="text-center body_medium_m">
                {I18n.t("Quarterly")}
              </div>
            </div>
            <div
              role="tab-item"
              className="switch_group_atoms md_switch_group_atoms"
              data-actived={selectedPeriod === "annual"}
              onClick={() => setSelectedPeriod("annual")}
            >
              <div className="text-center body_medium_m">
                {I18n.t("Annual")}
              </div>
            </div>
          </tab-indicator>

          <tab-indicator
            className="md_bill_switch bg-background_white_overlay10 md:[--capsule-bg-color:white] [--capsule-bg-color:var(--edgen-background\_brand\_overlay100)] [--capsule-border-radius:200px] [--animation-duration:0.2s]"
            effect="capsule"
          >
            <div
              role="tab-item"
              className="switch_group_atoms md_switch_group_atoms"
              data-actived={selectedMetric === "eps"}
              onClick={() => setSelectedMetric("eps")}
            >
              <div className="text-center body_medium_m">{I18n.t("EPS")}</div>
            </div>
            <div
              role="tab-item"
              className="switch_group_atoms md_switch_group_atoms"
              data-actived={selectedMetric === "revenue"}
              onClick={() => setSelectedMetric("revenue")}
            >
              <div className="text-center body_medium_m">
                {I18n.t("Revenue")}
              </div>
            </div>
          </tab-indicator>
        </div>
        <StockDetailTable
          data={displayData}
          columns={columns}
          getRowId={(row) => row.fiscal_period}
        />
      </div>
      {stockSymbol && (
        <ViewDetailsLink
          href={buildStockDetailModuleHref(stockSymbol, "earning")}
        />
      )}
    </>
  );
};
